import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useMenu, useRsMutation, type MenuItem } from '../api/restaurant';
import { formatGhs, parseCedis } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';

/* ------------------------------------------------------------------ extras on a dish */
const limitsText = (min: number, max: number): string => (min > 0 ? (max === 1 ? 'Choose one' : `Choose ${min === max ? max : `${min} to ${max}`}`) : max === 1 ? 'Optional, one only' : `Optional, up to ${max}`);

/** "Egg, 5.00" per line; the price is optional (free). */
export function parseExtras(text: string): { name: string; price: number }[] | null {
  const out: { name: string; price: number }[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const at = line.lastIndexOf(',');
    const name = (at >= 0 ? line.slice(0, at) : line).trim();
    const priceText = at >= 0 ? line.slice(at + 1).trim() : '';
    const price = priceText === '' ? 0 : parseCedis(priceText);
    if (!name || price === null) return null;
    out.push({ name, price });
  }
  return out.length > 0 && new Set(out.map((o) => o.name.toLowerCase())).size === out.length ? out : null;
}

export function ExtrasSection({ item }: { item: MenuItem }) {
  const toast = useToast();
  const report = useReport();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [required, setRequired] = useState(false);
  const [max, setMax] = useState('1');
  const [text, setText] = useState('');
  const makeGroup = useRsMutation((body: Record<string, unknown>) => api(`/menu/items/${item.id}/modifier-groups`, { method: 'POST', body }));
  const dropGroup = useRsMutation((id: string) => api(`/menu/modifier-groups/${id}`, { method: 'DELETE' }));
  const patchMod = useRsMutation((v: { id: string; body: Record<string, unknown> }) => api(`/menu/modifiers/${v.id}`, { method: 'PATCH', body: v.body }));
  const addMod = useRsMutation((v: { groupId: string; body: Record<string, unknown> }) => api(`/menu/modifier-groups/${v.groupId}/modifiers`, { method: 'POST', body: v.body }));
  const [more, setMore] = useState<Record<string, string>>({});

  const extras = parseExtras(text);
  const maxN = Math.min(10, Math.max(1, Number(max) || 1));
  const valid = name.trim().length > 0 && !!extras && (!required || extras.length >= 1);

  return (
    <div className="stack">
      <div className="eyebrow">Extras and choices</div>
      <p className="small muted">For things like spice level, a side to choose, or paid add-ons. Their prices are added to the dish price (tax included).</p>
      {item.modifierGroups.map((g) => (
        <div key={g.id} className="card flat stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div>
              <strong>{g.name}</strong>
              <div className="small muted">{limitsText(g.minChoices, g.maxChoices)}</div>
            </div>
            <Button variant="ghost" aria-label={`Remove ${g.name}`} onClick={() => dropGroup.mutate(g.id, { onError: report, onSuccess: () => toast.ok('Removed') })}>Remove</Button>
          </div>
          {g.modifiers.map((m) => (
            <div key={m.id} className="row" style={{ justifyContent: 'space-between' }}>
              <span>
                {m.name} {m.active ? null : <Badge>Hidden</Badge>}
              </span>
              <span className="row">
                <span className="num small muted">{m.price > 0 ? `+ ${formatGhs(m.price)}` : 'free'}</span>
                <Button variant="ghost" onClick={() => patchMod.mutate({ id: m.id, body: { active: !m.active } }, { onError: report })}>{m.active ? 'Hide' : 'Show'}</Button>
              </span>
            </div>
          ))}
          <form
            className="row"
            style={{ alignItems: 'flex-end' }}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const one = parseExtras(more[g.id] ?? '');
              if (one && one.length === 1) addMod.mutate({ groupId: g.id, body: one[0]! }, { onError: report, onSuccess: () => { toast.ok('Added'); setMore({ ...more, [g.id]: '' }); } });
            }}
          >
            <Field id={`am-${g.id}`} label="Add to this group" hint="Name, price (e.g. Fried egg, 5.00)">
              <input id={`am-${g.id}`} className="input" value={more[g.id] ?? ''} onChange={(e) => setMore({ ...more, [g.id]: e.target.value })} />
            </Field>
            <Button type="submit" disabled={parseExtras(more[g.id] ?? '')?.length !== 1} loading={addMod.isPending}>Add</Button>
          </form>
        </div>
      ))}
      {adding ? (
        <form
          className="stack card flat"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (!valid || !extras) return;
            makeGroup.mutate(
              { name: name.trim(), minChoices: required ? 1 : 0, maxChoices: maxN, modifiers: extras },
              { onError: report, onSuccess: () => { toast.ok('Added'); setAdding(false); setName(''); setText(''); setRequired(false); setMax('1'); } },
            );
          }}
        >
          <Field id="gn" label="Group name"><input id="gn" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spice level, Extras" /></Field>
          <label className="choice">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
            <span>The waiter must choose one</span>
            <span />
          </label>
          <Field id="gm" label="Most that can be chosen"><input id="gm" className="input" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} /></Field>
          <Field id="gt" label="Choices" hint="One per line: name, price (leave the price out if free)">
            <textarea id="gt" className="textarea" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Mild\nHot\nExtra hot'} />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!valid} loading={makeGroup.isPending}>Add group</Button>
          </div>
        </form>
      ) : (
        <Button onClick={() => setAdding(true)}>Add extras or choices</Button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ combos */
interface ComboRow {
  id: string;
  name: string;
  price: number;
  active: boolean;
  listPrice: number;
  components: { variantId: string; qty: number; itemName: string; variantName: string }[];
}

export function CombosTab() {
  const toast = useToast();
  const report = useReport();
  const combos = useQuery({ queryKey: ['menu', 'combos-admin'], queryFn: () => api<{ combos: ComboRow[] }>('/menu/combos').then((r) => r.combos) });
  const [adding, setAdding] = useState(false);
  const patch = useRsMutation((v: { id: string; body: Record<string, unknown> }) => api(`/menu/combos/${v.id}`, { method: 'PATCH', body: v.body }));
  const [price, setPrice] = useState<Record<string, string>>({});

  return (
    <>
      <div className="row">
        <Button variant="primary" onClick={() => setAdding(true)}>Add a combo</Button>
      </div>
      <p className="small muted">A combo is a set of dishes sold at one price. Each dish still goes to its own kitchen or bar and is taxed as food or drink; the set price is shared between them.</p>
      {combos.isLoading ? (
        <Loading rows={2} />
      ) : combos.error ? (
        <ErrorNote error={combos.error} retry={() => void combos.refetch()} />
      ) : (combos.data ?? []).length === 0 ? (
        <div className="card flat"><Empty icon="inbox" title="No combos yet">For example a lunch deal: a main and a drink for one price.</Empty></div>
      ) : (
        <div className="list">
          {(combos.data ?? []).map((c) => {
            const p = parseCedis(price[c.id] ?? '');
            return (
              <div key={c.id} className="item">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">{c.name}</span>
                  <span className="row">
                    {!c.active ? <Badge>Hidden</Badge> : null}
                    <span className="num">{formatGhs(c.price)}</span>
                  </span>
                </div>
                <span className="small muted">
                  {c.components.map((p2) => `${p2.qty > 1 ? `${p2.qty} × ` : ''}${p2.itemName}${p2.variantName !== 'Regular' ? ` (${p2.variantName})` : ''}`).join(' + ')} · bought separately {formatGhs(c.listPrice)}
                </span>
                <div className="row" style={{ alignItems: 'flex-end' }}>
                  <Field id={`cp-${c.id}`} label="Set price (GH₵)">
                    <input id={`cp-${c.id}`} className="input" inputMode="decimal" value={price[c.id] ?? (c.price / 100).toFixed(2)} onChange={(e) => setPrice({ ...price, [c.id]: e.target.value })} />
                  </Field>
                  <Button disabled={p === null || p === c.price || p < 1} onClick={() => patch.mutate({ id: c.id, body: { price: p } }, { onError: report, onSuccess: () => toast.ok('Price updated') })}>Save</Button>
                  <Button variant="ghost" onClick={() => patch.mutate({ id: c.id, body: { active: !c.active } }, { onError: report })}>{c.active ? 'Hide' : 'Show'}</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <NewCombo open={adding} onClose={() => setAdding(false)} />
    </>
  );
}

function NewCombo({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const menu = useMenu();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [rows, setRows] = useState<{ variantId: string; qty: string }[]>([{ variantId: '', qty: '1' }, { variantId: '', qty: '1' }]);
  const make = useRsMutation((body: Record<string, unknown>) => api('/menu/combos', { method: 'POST', body }));
  const sizes = (menu.data ?? []).flatMap((c) => c.items.flatMap((i) => i.variants.filter((v) => v.active).map((v) => ({ id: v.id, label: `${i.name}${i.variants.length > 1 ? ` · ${v.name}` : ''} (${formatGhs(v.price)})` }))));
  const p = parseCedis(price);
  const chosen = rows.filter((r) => r.variantId);
  const valid = name.trim().length > 0 && p !== null && p > 0 && chosen.length >= 2 && new Set(chosen.map((r) => r.variantId)).size === chosen.length;

  return (
    <Modal open={open} onClose={onClose} title="Add a combo">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate(
            { name: name.trim(), price: p, components: chosen.map((r) => ({ variantId: r.variantId, qty: Math.max(1, Number(r.qty) || 1) })) },
            { onError: report, onSuccess: () => { toast.ok(`${name.trim()} added`); setName(''); setPrice(''); setRows([{ variantId: '', qty: '1' }, { variantId: '', qty: '1' }]); onClose(); } },
          );
        }}
      >
        <Field id="cn2" label="Name"><input id="cn2" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Lunch deal" /></Field>
        <Field id="cpr" label="Set price (GH₵, tax included)"><input id="cpr" className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
        <div className="eyebrow">What is in it</div>
        {rows.map((r, i) => (
          <div key={i} className="row" style={{ alignItems: 'flex-end' }}>
            <Field id={`cv-${i}`} label={`Dish ${i + 1}`}>
              <select id={`cv-${i}`} className="select" value={r.variantId} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, variantId: e.target.value } : x)))}>
                <option value="">Choose…</option>
                {sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field id={`cq-${i}`} label="How many">
              <input id={`cq-${i}`} className="input" inputMode="numeric" value={r.qty} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/\D/g, '') } : x)))} />
            </Field>
          </div>
        ))}
        {rows.length < 8 ? <Button type="button" variant="ghost" onClick={() => setRows([...rows, { variantId: '', qty: '1' }])}>Add another dish</Button> : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Add combo</Button>
        </div>
      </form>
    </Modal>
  );
}
